import React from 'react';
import { BarChart3, Eye, Brain, CheckCircle, Target, TrendingUp } from 'lucide-react';

const ProgressAnimation = ({ currentStep, totalSteps = 6 }) => {
  const steps = [
    { id: 1, icon: <Eye className="w-5 h-5" />, label: "Preparing Upload", description: "Validating file and preparing for upload", percentage: 5 },
    { id: 2, icon: <Target className="w-5 h-5" />, label: "Uploading Video", description: "Transferring video file to server", percentage: 20 },
    { id: 3, icon: <Brain className="w-5 h-5" />, label: "Processing Video", description: "Extracting frames and analyzing content", percentage: 40 },
    { id: 4, icon: <TrendingUp className="w-5 h-5" />, label: "AI Analysis", description: "Gemini AI analyzing storytelling frameworks", percentage: 70 },
    { id: 5, icon: <BarChart3 className="w-5 h-5" />, label: "Generating Scores", description: "Calculating performance metrics and insights", percentage: 90 },
    { id: 6, icon: <CheckCircle className="w-5 h-5" />, label: "Finalizing Report", description: "Preparing detailed recommendations", percentage: 100 }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-600 to-purple-600 rounded-full mb-4">
            <TrendingUp className="w-8 h-8 text-white animate-pulse" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Analyzing Your Video
          </h3>
          <p className="text-gray-600 mb-2">
            Our AI is examining your content for optimization insights
          </p>
          <div className="text-sm text-gray-500">
            {currentStep <= 2 && "⏱️ Estimated time: 2-3 minutes"}
            {currentStep === 3 && "⏱️ Estimated time: 1-2 minutes remaining"}
            {currentStep === 4 && "⏱️ Estimated time: 30-60 seconds remaining"}
            {currentStep >= 5 && "⏱️ Almost done..."}
          </div>
        </div>

        {/* Progress Steps */}
        <div className="space-y-4 mb-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-500 ${
                currentStep >= step.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-400'
              }`}>
                {currentStep > step.id ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  step.icon
                )}
              </div>
              
              <div className="flex-1">
                <div className={`font-medium transition-colors duration-300 ${
                  currentStep >= step.id ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step.label}
                </div>
                <div className={`text-sm transition-colors duration-300 ${
                  currentStep >= step.id ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {step.description}
                </div>
                {currentStep === step.id && (
                  <div className="text-xs text-primary-600 mt-1 font-medium">
                    {step.id === 1 && "Validating file format and size..."}
                    {step.id === 2 && "Uploading video data..."}
                    {step.id === 3 && "Extracting key frames and metadata..."}
                    {step.id === 4 && "Running AI analysis on content..."}
                    {step.id === 5 && "Calculating performance scores..."}
                    {step.id === 6 && "Generating final report..."}
                  </div>
                )}
              </div>
              
              {/* Animated dot for current step */}
              {currentStep === step.id && (
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{steps.find(step => step.id === currentStep)?.percentage || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-primary-600 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${steps.find(step => step.id === currentStep)?.percentage || 0}%` }}
            ></div>
          </div>
        </div>

        {/* Loading Animation */}
        <div className="flex justify-center">
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-primary-600 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-primary-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-3 h-3 bg-primary-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressAnimation;
